import axios, { AxiosError, AxiosResponse, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import axiosInstance from '../../../configs/axios';

class JwtService {
  [x: string]: any;
  init() {
    this.setInterceptors();
    this.handleAuthentication();
  }

  setInterceptors() {
    axiosInstance.interceptors.request.use(this.requestInterceptor, (error) => Promise.reject(error));
    axiosInstance.interceptors.response.use(this.responseInterceptor, this.responseErrorInterceptor);
  }

  requestInterceptor = async (config: AxiosRequestConfig): Promise<InternalAxiosRequestConfig<unknown>> => {
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();
    
    if (accessToken && refreshToken && config.url && !config.url.includes('/admin/login')) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${accessToken}`,
        'refresh-token': refreshToken,
      };
    }
    return config as InternalAxiosRequestConfig<unknown>;
  };

  responseInterceptor = (response: AxiosResponse) => {
    const accessToken = response.headers['access-token'];
    if (accessToken) {
      const token = accessToken.split(' ')[1];
      if (token !== this.getAccessToken()) {
        this.setAccessToken(token);
      }
    }
    return response;
  };

  responseErrorInterceptor = (error: AxiosError) => {
    if (error?.response?.status === 401) {
      this.logout();
    }
    return Promise.reject(error);
  };

  handleAuthentication() {
    if (!this.getRefreshToken() || !this.getAccessToken()) {
      this.emit('onNoAccessToken');
    } else {
      this.emit('onAutoLogin', true);
    }
  }

  signInWithApiInstance(email: string, password: string): Promise<any> {
    return axiosInstance.post('/admin/login', { email, password })
      .then(({ data }) => {
        if (data.code === 200 && data.data.token) {
          this.setAccessToken(data.data.token);
          this.setRefreshToken(data.data.refresh_token);
          return data;
        }
        throw new Error(data.message || 'Admin login failed');
      });
  }

  signInWithToken(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (this.getRefreshToken() && this.getAccessToken()) {
        // Just validate if we can access admin endpoints
        axiosInstance.get('/admin/users')
          .then(({ data }) => {
            if (data.code === 200) {
              // Get admin user data from localStorage
              const adminUserData = localStorage.getItem('adminUserData');
              if (adminUserData) {
                resolve(JSON.parse(adminUserData));
              } else {
                // If no stored user data, reject to force re-login
                throw new Error('No admin user data found');
              }
            } else {
              throw new Error('Token validation failed');
            }
          })
          .catch((error) => {
            console.log('Admin token validation failed:', error);
            this.logout();
            reject(new Error('Failed to login with token.'));
          });
      } else {
        this.logout();
        reject(new Error('Failed to login with token.'));
      }
    });
  }

  logout() {
    this.clearSession();
    // this.emit('onLogout', 'Logged out');
  }

  getAccessToken() {
    return localStorage.getItem('admin_access_token');
  }

  getRefreshToken() {
    return localStorage.getItem('admin_refresh_token');
  }

  setAccessToken(token: string) {
    localStorage.setItem('admin_access_token', token);
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  }

  setRefreshToken(token: string) {
    localStorage.setItem('admin_refresh_token', token);
  }

  clearSession() {
    localStorage.removeItem('admin_access_token');
    localStorage.removeItem('admin_refresh_token');
    localStorage.removeItem('adminUserData');
    delete axios.defaults.headers.common.Authorization;
  }
}

export default new JwtService();